import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Check, Calendar, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// CALENDLY CONFIGURATION
// ============================================
const CALENDLY_URL = 'https://calendly.com/glowhealthsupport-info/glow-health-initial-consultation';
// ============================================

const Schedule = () => {
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [verificationDetails, setVerificationDetails] = useState(null);
  const [error, setError] = useState(null);
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);
  const sessionId = searchParams.get('session_id');
  const previewMode = searchParams.get('preview') === 'true';

  // Load Calendly script
  useEffect(() => {
    if (paymentStatus === 'success' && !calendlyLoaded) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.onload = () => setCalendlyLoaded(true);
      document.body.appendChild(script);

      return () => {
        const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
        if (existingScript) {
          document.body.removeChild(existingScript);
        }
      };
    }
  }, [paymentStatus, calendlyLoaded]);

  useEffect(() => {
    // Preview mode for testing UI
    if (previewMode) {
      setPaymentStatus('success');
      setVerificationDetails({ verified: true, payment_status: 'paid', status: 'complete' });
      return;
    }

    const verifyPayment = async () => {
      // Check if session_id is present
      if (!sessionId) {
        console.error('No session_id found in URL');
        setError({
          type: 'missing_session_id',
          message: 'No payment session found. Please complete the intake form and payment first.',
          details: 'The session_id parameter is missing from the URL.'
        });
        setPaymentStatus('no_session');
        return;
      }

      console.log('Verifying payment for session:', sessionId);

      try {
        // Use the new verify endpoint for direct Stripe verification
        const response = await axios.get(`${API}/payments/verify/${sessionId}`);
        const data = response.data;
        
        console.log('Verification response:', data);
        setVerificationDetails(data);

        if (data.verified) {
          console.log('Payment verified successfully');
          setPaymentStatus('success');
        } else if (data.error) {
          // Handle specific error types
          console.error('Verification failed:', data.error, data.error_message);
          setError({
            type: data.error,
            message: getErrorMessage(data.error),
            details: data.error_message
          });
          setPaymentStatus('error');
        } else if (data.payment_status === 'unpaid') {
          console.log('Payment not yet completed');
          setError({
            type: 'payment_incomplete',
            message: 'Payment was not completed. Please try again.',
            details: `Session status: ${data.status}, Payment status: ${data.payment_status}`
          });
          setPaymentStatus('error');
        } else {
          // Unknown status
          setError({
            type: 'unknown',
            message: 'Unable to verify payment status.',
            details: `Status: ${data.status}, Payment: ${data.payment_status}`
          });
          setPaymentStatus('error');
        }
      } catch (err) {
        console.error('Error verifying payment:', err);
        const errorMessage = err.response?.data?.detail || err.message || 'Unknown error';
        setError({
          type: 'api_error',
          message: 'Unable to verify payment. Please contact support.',
          details: errorMessage
        });
        setPaymentStatus('error');
      }
    };

    verifyPayment();
  }, [sessionId, previewMode]);

  // Helper function to get user-friendly error messages
  const getErrorMessage = (errorType) => {
    switch (errorType) {
      case 'missing_env_var':
        return 'Payment system is not properly configured. Please contact support.';
      case 'missing_session_id':
        return 'No payment session found. Please complete the intake form and payment.';
      case 'invalid_session':
        return 'This payment session is invalid or has expired. Please start a new intake.';
      case 'auth_error':
        return 'Payment verification failed. Please contact support.';
      default:
        return 'Unable to verify payment. Please contact support if this continues.';
    }
  };

  // Loading state
  if (paymentStatus === 'checking') {
    return (
      <div className="page-schedule">
        <div className="schedule-container">
          <div className="schedule-card">
            <div className="schedule-loading">
              <div className="loading-spinner"></div>
              <p>Verifying your payment...</p>
              {sessionId && <p className="loading-session-id">Session: {sessionId.substring(0, 20)}...</p>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No session ID - prevent access without payment
  if (paymentStatus === 'no_session') {
    return (
      <div className="page-schedule">
        <div className="schedule-container">
          <div className="schedule-card">
            <div className="schedule-error">
              <AlertTriangle className="error-icon" />
              <h1 className="heading-2">Complete Payment First</h1>
              <p className="body-regular">
                {error?.message || 'To schedule your consultation, please complete the intake form and payment first.'}
              </p>
              {error?.details && (
                <p className="error-details">{error.details}</p>
              )}
              <Link to="/intake">
                <button className="btn-primary">Start Intake</button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Payment verification failed
  if (paymentStatus === 'error') {
    return (
      <div className="page-schedule">
        <div className="schedule-container">
          <div className="schedule-card">
            <div className="schedule-error">
              <AlertTriangle className="error-icon" />
              <h1 className="heading-2">Payment Verification Issue</h1>
              <p className="body-regular">
                {error?.message || 'There was an issue verifying your payment.'}
              </p>
              {error?.details && (
                <div className="error-details-box">
                  <p className="error-details-label">Technical Details:</p>
                  <p className="error-details">{error.details}</p>
                </div>
              )}
              {verificationDetails && (
                <div className="verification-info">
                  <p>Status: {verificationDetails.status || 'Unknown'}</p>
                  <p>Payment: {verificationDetails.payment_status || 'Unknown'}</p>
                </div>
              )}
              <div className="error-actions">
                <Link to="/intake">
                  <button className="btn-primary">Try Again</button>
                </Link>
                <a href="mailto:hello@glowhealth.com" className="btn-secondary">
                  Contact Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Payment successful - show Calendly scheduling
  return (
    <div className="page-schedule">
      <div className="schedule-container-wide">
        <div className="schedule-card">
          {/* Success Header */}
          <div className="schedule-success-header">
            <div className="success-icon-circle">
              <Check className="success-check-icon" />
            </div>
            <h1 className="heading-2">Welcome to Glow Health!</h1>
            <p className="body-regular">
              Your membership is confirmed. Now let&apos;s schedule your Initial Consultation.
            </p>
            {verificationDetails?.customer_email && (
              <p className="confirmation-email">Confirmation sent to: {verificationDetails.customer_email}</p>
            )}
          </div>

          {/* Calendly Embed Section */}
          <div className="schedule-content">
            <div className="schedule-info">
              <h2 className="heading-3">Schedule Your Consultation</h2>
              <p className="body-regular">
                Choose a time that works for you. Your provider will review your intake form 
                before your appointment so you can make the most of your time together.
              </p>
            </div>

            {/* Calendly Inline Widget */}
            <div className="calendly-embed-container">
              <div 
                className="calendly-inline-widget" 
                data-url={CALENDLY_URL}
                style={{ minWidth: '320px', height: '700px' }}
              />
              {!calendlyLoaded && (
                <div className="calendly-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading scheduling calendar...</p>
                </div>
              )}
            </div>

            {/* Labs Info */}
            <div className="schedule-labs-info">
              <h3 className="labs-info-title">About Lab Work</h3>
              <p>
                To make the most of your consultation, your provider may recommend lab work based on your intake.
              </p>
              <p>
                If you already have recent labs, you may upload them ahead of time. If not, your provider 
                will guide you during your appointment. <strong>Labs are not required before your visit.</strong>
              </p>
            </div>
          </div>

          {/* Home Link */}
          <div className="schedule-footer">
            <Link to="/" className="btn-text">
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Schedule;
